import { c as _c } from "react/compiler-runtime";
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $wrapNodeInElement, mergeRegister } from '@lexical/utils';
import { $createParagraphNode, $isRootOrShadowRoot, COMMAND_PRIORITY_EDITOR, PASTE_COMMAND, COMMAND_PRIORITY_HIGH, createCommand, LexicalCommand, DROP_COMMAND } from 'lexical';
import { DRAG_DROP_PASTE } from '@lexical/rich-text';
import { useEffect } from 'react';
import { $createImageNode, ImageNode } from './ImageNode';
import { createEl } from 'shared/document';
import { notNull } from 'shared/type';
import { usePortal } from 'components/portal/portal';
import { showError } from 'components/notify';
import { isString } from 'remeda';
import { $insertNodeToNearestRoot } from '../utils';
import { asyncMap } from 'modern-async';
import { P, match } from 'ts-pattern';
import { customAlphabet, urlAlphabet } from 'nanoid';
import { sb } from 'utils/db';
const makeId = customAlphabet(urlAlphabet, 10);
export type InsertImagePayload = {
  images: File[];
};
export const IMAGE_INSERT_CMD: LexicalCommand<InsertImagePayload> = createCommand('INSERT_IMAGE_COMMAND');
export function ImagePlugin() {
  const $ = _c(8);
  const [editor] = useLexicalComposerContext();
  const {
    profile
  } = usePortal();
  let t0;
  if ($[0] !== editor || $[1] !== profile) {
    t0 = async props => {
      const {
        node
      } = props;
      if (!profile) {
        return;
      }
      if (node.getImg().state != "uploading") {
        editor.update(() => {
          node.updateImg({
            state: "uploading"
          });
        });
      }
      const file_0 = await match(props).with({
        file: P.any
      }, _temp).with({
        url: P.any
      }, t1 => {
        const {
          url
        } = t1;
        return imageDownload(url).catch(e => {
          console.warn("error downloading image", e);
          editor.update(() => {
            node.updateImg({
              state: "external",
              src: url
            });
          });
        });
      }).exhaustive();
      if (!file_0) {
        return;
      }
      const path = `${profile.id}/${makeId()}.${file_0.type.split("/")[1]}`;
      const up = sb.storage.from("upload");
      up.upload(path, file_0).then(upRes => {
        if (upRes.error) {
          showError(upRes.error, "Error uploading image");
          return;
        }
        const src = up.getPublicUrl(path).data.publicUrl;
        editor.update(() => {
          node.updateImg({
            state: "uploaded",
            src
          });
        });
      });
    };
    $[0] = editor;
    $[1] = profile;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const uploadImage = t0;
  let t1;
  if ($[3] !== editor || $[4] !== uploadImage) {
    t1 = () => {
      if (!editor.hasNodes([ImageNode])) {
        throw new Error("ImagesPlugin: ImageNode not registered on editor");
      }
      const insertImage = file_1 => new Promise((resolve, reject) => {
        const init = async () => {
          const src_0 = await readAsDataURL(file_1);
          return {
            dim: await imageDimensions(src_0),
            src: src_0
          };
        };
        init().then(t2 => {
          const {
            dim,
            src: src_1
          } = t2;
          const width = Math.min(dim.width, ImageNode.DefaultWidth);
          editor.update(() => {
            const node_0 = $insertNodeToNearestRoot($createImageNode({
              state: "uploading",
              srcPlaceholder: src_1,
              width
            } as const));
            if ($isRootOrShadowRoot(node_0.getParentOrThrow())) {
              $wrapNodeInElement(node_0, $createParagraphNode).selectNext();
            }
            uploadImage({
              file: file_1,
              node: node_0
            }).then(() => {
              resolve(node_0);
            });
          }, {});
        });
      });
      return mergeRegister(editor.registerCommand(IMAGE_INSERT_CMD, t3 => {
        const {
          images
        } = t3;
        asyncMap(images, insertImage, 4);
        return true;
      }, COMMAND_PRIORITY_EDITOR), editor.registerCommand(DRAG_DROP_PASTE, files => {
        const images_0 = files.filter(notNull).filter(isImage);
        if (!files.length) {
          return false;
        }
        editor.dispatchCommand(IMAGE_INSERT_CMD, {
          images: images_0
        });
        return true;
      }, COMMAND_PRIORITY_HIGH), editor.registerCommand(PASTE_COMMAND, e_0 => {
        const items = e_0.clipboardData?.items ? Array.from(e_0.clipboardData?.items) : undefined;
        if (!items) {
          return false;
        }
        if (items.some(_temp2)) {
          return false;
        }
        const images_1 = items.filter(_temp3).map(_temp4).filter(notNull).filter(isImage);
        if (!images_1.length) {
          return false;
        }
        editor.dispatchCommand(IMAGE_INSERT_CMD, {
          images: images_1
        });
        return true;
      }, COMMAND_PRIORITY_HIGH), editor.registerNodeTransform(ImageNode, node_1 => {
        const img = node_1.getImg();
        if (img.state == "pasted" && img.src.startsWith("http")) {
          uploadImage({
            node: node_1,
            url: img.src
          });
        }
      }));
    };
    $[3] = editor;
    $[4] = uploadImage;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== editor) {
    t2 = [editor];
    $[6] = editor;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  useEffect(t1, t2);
  return null;
}
function _temp4(item) {
  return item.getAsFile();
}
function _temp3(i_0) {
  return i_0.kind == "file";
}
function _temp2(i) {
  return i.kind == "string-text";
}
function _temp(t0) {
  const {
    file
  } = t0;
  return Promise.resolve(file);
}
const isImage = (file: File) => isString(file.type) && file.type.startsWith('image/');
const readAsDataURL = async (file: File) => new Promise<string>((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result as string);
  r.onerror = reject;
  r.readAsDataURL(file);
});
const imageDimensions = (src: string) => new Promise<{
  width: number;
  height: number;
}>((resolve, reject) => {
  const imgEl = createEl('img', {
    src,
    crossOrigin: 'anonymous'
  });
  imgEl.onload = () => {
    resolve({
      width: imgEl.naturalWidth,
      height: imgEl.naturalHeight
    });
  };
  imgEl.onerror = reject;
});
const imageDownload = (src: string) => fetch(src).then(r => r?.blob());